import { fetchUtils } from 'react-admin';
import restProvider from 'ra-data-simple-rest';

const baseUrl = `${process.env.REACT_APP_BASE_URL}/cms`;

const httpClient = (url, options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  const { access_token } = JSON.parse(
    localStorage.getItem('auth') || '{"access_token": ""}',
  );
  options.headers.set('Authorization', `Bearer ${access_token}`);
  return fetchUtils.fetchJson(url, options);
};

const dataProvider = restProvider(baseUrl, httpClient);

const appendFile = (field, formData, file) => {
  if (file && typeof file != 'string') {
    formData.append(field, file.rawFile);
  } else if (file) {
    formData.append(field, file);
  }
};

const appendTranslation = (field, formData, value) => {
  if (value) {
    formData.append(field, value);
  }
};

// Campos de texto/booleanos/data de prediction-campaigns que vão direto pro
// FormData (os 3 campos de imagem são tratados à parte via appendFile).
const PREDICTION_CAMPAIGN_TEXT_FIELDS = [
  'slug',
  'title',
  'subtitle',
  'intro_title',
  'intro_description',
  'intro_button_text',
  'intro_title_line1',
  'intro_title_line2',
  'intro_year_text',
  'feedback_title',
  'feedback_description',
  'ranking_title',
  'how_it_works_title',
  'how_it_works_description',
  'matches_section_title',
  'top_four_section_title',
  'top_four_first_label',
  'top_four_second_label',
  'top_four_third_label',
  'top_four_fourth_label',
  'top_four_first_subtitle',
  'top_four_second_subtitle',
  'top_four_third_subtitle',
  'top_four_fourth_subtitle',
  'top_four_note',
  'card_title',
  'card_description',
  'primary_color',
  'secondary_color',
  'starts_at',
  'ends_at',
];

const appendPredictionCampaignFields = (formData, data) => {
  PREDICTION_CAMPAIGN_TEXT_FIELDS.forEach((field) => {
    if (data[field] !== undefined && data[field] !== null) {
      formData.append(field, data[field]);
    }
  });

  formData.append('active', data.active ?? false);
  formData.append('top_four_enabled', data.top_four_enabled ?? true);
  formData.append('ranking_enabled', data.ranking_enabled ?? true);

  // Sempre enviado (mesmo vazio) — o formulário é a fonte da verdade da
  // lista de elegibilidade: salvar com a seleção vazia limpa a lista
  // (volta ao fallback de "todos os times ativos" no seletor do app).
  formData.append(
    'top_four_team_ids',
    JSON.stringify(data.top_four_team_ids || []),
  );

  // appendFile já diferencia arquivo novo (objeto com rawFile) de URL/key
  // existente (string) — se o campo não vier (undefined/null), simplesmente
  // não é anexado, e o backend preserva o valor atual.
  appendFile('card_image', formData, data.card_image);
  appendFile('intro_image', formData, data.intro_image);
  appendFile('background_image', formData, data.background_image);
};

const appendPredictionTeamFields = (formData, data) => {
  formData.append('name', data.name);
  formData.append('slug', data.slug);
  formData.append('type', data.type);
  formData.append('active', data.active ?? true);

  // appendFile diferencia arquivo novo (objeto com rawFile) de URL/key
  // existente (string) — se o campo não vier (undefined/null), não é
  // anexado, e o backend preserva a imagem atual.
  appendFile('image', formData, data.image);
};

const myDataProvider = {
  ...dataProvider,
  getOne: (resource, params) => {
    if (resource != 'banners') {
      return dataProvider.getOne(resource, params);
    }

    return httpClient(`${baseUrl}/${resource}/${params.id}`).then(
      ({ json }) => {
        let namePtBR = json.ptbr_name.split(';:,');
        let nameEnUS = json.enus_name.split(';:,');
        let nameEs = json.es_name.split(';:,');
        let nameHu = json.hu_name.split(';:,');

        return {
          data: {
            ...json,
            ptbr_name: namePtBR[0],
            ptbr_sub: namePtBR[1],
            ptbr_desc: namePtBR[2],
            enus_name: nameEnUS[0],
            enus_sub: nameEnUS[1],
            enus_desc: nameEnUS[2],
            es_name: nameEs[0],
            es_sub: nameEs[1],
            es_desc: nameEs[2],
            hu_name: nameHu[0],
            hu_sub: nameHu[1],
            hu_desc: nameHu[2],
          },
        };
      },
    );
  },
  update: (resource, params) => {
    if (
      resource !== 'users' &&
      resource !== 'banners' &&
      resource !== 'lessons' &&
      resource !== 'prediction-campaigns' &&
      resource !== 'prediction-teams'
    ) {
      return dataProvider.update(resource, params);
    }

    if (resource === 'prediction-campaigns') {
      const formData = new FormData();
      appendPredictionCampaignFields(formData, params.data);

      return httpClient(`${baseUrl}/${resource}/${params.id}`, {
        method: 'PUT',
        body: formData,
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }

    if (resource === 'prediction-teams') {
      const formData = new FormData();
      appendPredictionTeamFields(formData, params.data);

      return httpClient(`${baseUrl}/${resource}/${params.id}`, {
        method: 'PUT',
        body: formData,
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }

    if (resource === 'users') {
      const data = {};
      if (params.data.is_blocked != null) {
        data.is_blocked = params.data.is_blocked;
      }
      if (data.is_blocked && params.data.expire_block) {
        data.expire_block = params.data.expire_block;
      }
      if (params.data.is_premium != null) {
        data.is_premium = params.data.is_premium;
      }

      return dataProvider.update(resource, {
        ...params,
        data: data,
      });
    } else if (resource === 'banners') {
      let formData = new FormData();
      formData.append('name', params.data.name);
      formData.append('type', params.data.type);
      formData.append('active', params.data.active);
      formData.append('priority', params.data.priority);

      let data = params.data;
      let namePtBR =
        data['ptbr_name'] +
        ';:,' +
        data['ptbr_sub'] +
        ';:,' +
        data['ptbr_desc'];
      let nameEnUS =
        data['enus_name'] +
        ';:,' +
        data['enus_sub'] +
        ';:,' +
        data['enus_desc'];
      let nameEs =
        data['es_name'] + ';:,' + data['es_sub'] + ';:,' + data['es_desc'];
      // let nameHu = data['hu_name'] + ";:," + data['hu_sub'] + ";:," + data['hu_desc'];

      formData.append('ptbr-name', namePtBR);
      formData.append('enus-name', nameEnUS);
      formData.append('es-name', nameEs);
      formData.append('hu-name', nameEnUS);

      formData.append('target_id', params.data['target_id']);
      if (params.data['date_start']) {
        formData.append('date_start', params.data['date_start']);
      }
      if (params.data['date_end']) {
        formData.append('date_end', params.data['date_end']);
      }
      // Old
      // if (params.data.image.rawFile) {
      //   formData.append('image', params.data.image.rawFile);
      // }

      if (params.data.image?.rawFile) {
        formData.append('image', params.data.image.rawFile);
      } else if (typeof params.data.image === 'string') {
        // Manter a URL existente
        formData.append('image', params.data.image);
      }

      return httpClient(`${baseUrl}/${resource}/${params.id}`, {
        method: 'PUT',
        body: formData,
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    } else if (resource === 'lessons') {
      let formData = new FormData();
      formData.append('blocked', params.data.blocked || false);
      formData.append('name', params.data.name);
      formData.append('xp', params.data.xp);
      formData.append('division-id', parseInt(params.data.division));
      formData.append('premium', params.data.premium);
      if (params.data['date_start']) {
        formData.append('date_start', params.data['date_start']);
      }
      if (params.data['date_end']) {
        formData.append('date_end', params.data['date_end']);
      }

      appendTranslation('ptbr-name', formData, params.data['ptbr_name']);
      appendTranslation('enus-name', formData, params.data['enus_name']);
      appendTranslation('es-name', formData, params.data['es_name']);
      appendTranslation('hu-name', formData, params.data['enus_name']);

      appendFile('ptbr-video', formData, params.data['ptbr_video']);
      appendFile('enus-video', formData, params.data['enus_video']);
      appendFile('es-video', formData, params.data['es_video']);
      appendFile('hu-video', formData, params.data['enus_video']);

      appendFile('thumbnail', formData, params.data.thumbnail);

      return httpClient(`${baseUrl}/${resource}/${params.id}`, {
        method: 'PUT',
        body: formData,
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }
  },

  create: (resource, params) => {
    if (
      resource !== 'lessons' &&
      resource !== 'banners' &&
      resource !== 'prediction-campaigns' &&
      resource !== 'prediction-teams'
    ) {
      return dataProvider.create(resource, params);
    }

    let formData = new FormData();

    if (resource === 'prediction-campaigns') {
      appendPredictionCampaignFields(formData, params.data);

      return httpClient(`${baseUrl}/${resource}`, {
        method: 'POST',
        body: formData,
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }

    if (resource === 'prediction-teams') {
      appendPredictionTeamFields(formData, params.data);

      return httpClient(`${baseUrl}/${resource}`, {
        method: 'POST',
        body: formData,
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }

    if (resource === 'lessons') {
      formData.append('name', params.data.name);
      formData.append('xp', params.data.xp);
      formData.append('division-id', parseInt(params.data.division));
      formData.append('premium', params.data.premium);

      appendTranslation('ptbr-name', formData, params.data['ptbr-name']);
      appendTranslation('enus-name', formData, params.data['enus-name']);
      appendTranslation('es-name', formData, params.data['es-name']);
      appendTranslation('hu-name', formData, params.data['enus-name']);

      appendFile('ptbr-video', formData, params.data['ptbr-video']);
      appendFile('enus-video', formData, params.data['enus-video']);
      appendFile('es-video', formData, params.data['es-video']);
      appendFile('hu-video', formData, params.data['enus-video']);

      appendFile('thumbnail', formData, params.data.thumbnail);

      if (params.data['date-start']) {
        formData.append('date_start', params.data['date-start']);
      }
      if (params.data['date-end']) {
        formData.append('date_end', params.data['date-end']);
      }
    } else if (resource === 'banners') {
      formData.append('name', params.data.name);
      formData.append('type', params.data.type);
      formData.append('active', params.data.active);
      formData.append('priority', params.data.priority);

      let data = params.data;
      let namePtBR =
        data['ptbr-name'] +
        ';:,' +
        data['ptbr-sub'] +
        ';:,' +
        data['ptbr-desc'];
      let nameEnUS =
        data['enus-name'] +
        ';:,' +
        data['enus-sub'] +
        ';:,' +
        data['enus-desc'];
      let nameEs =
        data['es-name'] + ';:,' + data['es-sub'] + ';:,' + data['es-desc'];
      // let nameHu = data['hu-name'] + ";:," + data['hu-sub'] + ";:," + data['hu-desc'];

      formData.append('ptbr-name', namePtBR);
      formData.append('enus-name', nameEnUS);
      formData.append('es-name', nameEs);
      formData.append('hu-name', nameEnUS);

      formData.append('target_id', params.data.target_id);
      if (params.data['date-start']) {
        formData.append('date_start', params.data['date-start']);
      }
      if (params.data['date-end']) {
        formData.append('date_end', params.data['date-end']);
      }
      formData.append('image', params.data.image.rawFile);
    }

    return httpClient(`${baseUrl}/${resource}`, {
      method: 'POST',
      body: formData,
    }).then(({ json }) => ({
      data: { ...params.data, id: json.id },
    }));
  },
};

export default myDataProvider;
